import { useState, useEffect } from 'react';
import { X, FileDown, Check, Save, Trash2, Loader2 } from 'lucide-react';
import { Transaction } from '../../types/dashboard';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import * as XLSX from 'xlsx';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactions: Transaction[];
}

interface ExportColumn {
  key: string;
  label: string;
  checked: boolean;
  getValue: (transaction: Transaction) => string;
}

interface SavedFilter {
  id: string;
  name: string;
  columns: string[];
}

const DEFAULT_COLUMNS: ExportColumn[] = [
  { key: 'property_number', label: 'N° Bien', checked: true, getValue: t => t.property_number },
  { key: 'agencies', label: 'Agence(s)', checked: true, getValue: t => t.agencies.join(', ') },
  { key: 'negotiators', label: 'Négociateur(s)', checked: true, getValue: t => t.negotiators.map(n => `${n.name} (${n.role} - ${n.percentage}%)`).join(', ') },
  { key: 'seller', label: 'Vendeur', checked: true, getValue: t => t.seller },
  { key: 'buyer', label: 'Acquéreur', checked: true, getValue: t => t.buyer },
  { key: 'sale_price', label: 'Prix de vente', checked: true, getValue: t => t.sale_price.toString() },
  { key: 'fees_with_tax', label: 'Honoraires TTC', checked: true, getValue: t => t.fees_with_tax.toString() },
  { key: 'fees_without_tax', label: 'Honoraires HT', checked: true, getValue: t => t.fees_without_tax.toString() },
  { key: 'status', label: 'Statut', checked: true, getValue: t => t.status },
  { key: 'mandate_type', label: 'Type de mandat', checked: false, getValue: t => t.mandate_type || '' },
  { key: 'compromise_date', label: 'Date de compromis', checked: false, getValue: t => t.compromise_date ? new Date(t.compromise_date).toLocaleDateString('fr-FR') : '' },
  { key: 'expected_acte_date', label: 'Date prévisionnelle acte', checked: false, getValue: t => t.expected_acte_date ? new Date(t.expected_acte_date).toLocaleDateString('fr-FR') : '' },
  { key: 'acte_date', label: 'Date de l\'acte', checked: false, getValue: (t: any) => {
    console.log('Export acte_date for transaction:', t.property_number, 'value:', t.acte_date);
    return t.acte_date ? new Date(t.acte_date).toLocaleDateString('fr-FR') : '';
  }},
  { key: 'invoice_date', label: 'Date de facture', checked: false, getValue: (t: any) => {
    console.log('Export invoice_date for transaction:', t.property_number, 'value:', t.invoice_date);
    return t.invoice_date ? new Date(t.invoice_date).toLocaleDateString('fr-FR') : '';
  }},
  { key: 'invoice_number', label: 'Numéro de facture', checked: false, getValue: (t: any) => {
    console.log('Export invoice_number for transaction:', t.property_number, 'value:', t.invoice_number);
    return t.invoice_number || '';
  }},
  { key: 'encaissement_date', label: 'Date d\'encaissement', checked: false, getValue: (t: any) => {
    console.log('Export encaissement_date for transaction:', t.property_number, 'value:', t.encaissement_date);
    return t.encaissement_date ? new Date(t.encaissement_date).toLocaleDateString('fr-FR') : '';
  }},
  { key: 'cancelled_at', label: 'Date d\'annulation système', checked: false, getValue: (t: any) => {
    console.log('🔍 Export cancelled_at for transaction:', t.property_number, 'Raw value:', t.cancelled_at, 'Type:', typeof t.cancelled_at);
    return t.cancelled_at ? new Date(t.cancelled_at).toLocaleDateString('fr-FR') : '';
  }},
  { key: 'property_origin', label: 'Origine du bien', checked: false, getValue: t => {
    switch (t.property_origin) {
      case 'gestion': return 'Bien de gestion';
      case 'syndic': return 'Bien de syndic';
      case 'gestion_lefeuvre': return 'Bien de gestion Lefeuvre';
      case 'syndic_lefeuvre': return 'Bien de syndic Lefeuvre';
      default: return t.property_origin || '';
    }
  }},
  { key: 'copro_details', label: 'Copropriété', checked: false, getValue: t => {
    if (t.copros && t.copros.numero && t.copros.residence) {
      return `${t.copros.numero} - ${t.copros.residence}`;
    }
    return '';
  }},
  { key: 'net_fees_without_tax', label: 'Honoraires Net HT', checked: false, getValue: t => {
    const totalRetrocessions = t.retrocessions?.reduce((sum, r) => sum + r.amount, 0) || 0;
    const netFees = t.fees_without_tax - totalRetrocessions;
    return netFees.toString();
  }},
  { key: 'regul', label: 'Régularisation en cours', checked: false, getValue: t => t.regul ? 'Oui' : 'Non' },
  { key: 'regul_comment', label: 'Commentaire régularisation', checked: false, getValue: t => {
    console.log('🔍🔍 Export regul_comment for transaction:', t.property_number);
    console.log('   - Raw value:', t.regul_comment);
    console.log('   - Type:', typeof t.regul_comment);
    console.log('   - Length:', t.regul_comment ? t.regul_comment.length : 'null/undefined');
    console.log('   - Is null?', t.regul_comment === null);
    console.log('   - Is undefined?', t.regul_comment === undefined);
    console.log('   - Is empty string?', t.regul_comment === '');
    console.log('   - Truthiness:', !!t.regul_comment);
    console.log('   - Regul status:', t.regul);
    return t.regul_comment || '';
  }},
  { key: 'approved', label: 'Dossier validé par admin', checked: false, getValue: t => t.approved ? 'Oui' : 'Non' },
  { key: 'approved_by_name', label: 'Validé par (nom admin)', checked: false, getValue: t => t.approved_by_name || '' },
  { key: 'approved_at', label: 'Date validation admin', checked: false, getValue: t => t.approved_at ? new Date(t.approved_at).toLocaleDateString('fr-FR') : '' },
  { key: 'assistants', label: 'Assistantes (entrante/sortante)', checked: false, getValue: t => t.assistants?.map(a => `${a.name} (${a.role})`).join(', ') || '' },
  { key: 'retrocessions', label: 'Détail des rétrocessions', checked: false, getValue: t => t.retrocessions?.map(r => `${r.type}: ${r.amount}€${r.payment_date ? ` (payé le ${new Date(r.payment_date).toLocaleDateString('fr-FR')})` : ''}`).join(', ') || '' }
];

export default function ExportModal({ isOpen, onClose, transactions }: ExportModalProps) {
  const { user } = useAuth();
  const [columns, setColumns] = useState<ExportColumn[]>(DEFAULT_COLUMNS);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [newFilterName, setNewFilterName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Add escape key handler
  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  // Charger les filtres sauvegardés au démarrage
  useEffect(() => {
    if (user) {
      loadSavedFilters();
    }
  }, [user]);

  const loadSavedFilters = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('export_filters')
        .select('id, name, columns')
        .eq('user_id', user?.id)
        .order('name');

      if (error) throw error;

      setSavedFilters(data.map(filter => ({
        id: filter.id,
        name: filter.name,
        columns: filter.columns as string[]
      })));
    } catch (err) {
      console.error('Error loading filters:', err);
      setError('Erreur lors du chargement des filtres');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAll = () => {
    const allChecked = columns.every(col => col.checked);
    setColumns(columns.map(col => ({ ...col, checked: !allChecked })));
  };

  const handleToggleColumn = (key: string) => {
    setColumns(columns.map(col => 
      col.key === key ? { ...col, checked: !col.checked } : col
    ));
  };

  const handleSaveFilter = async () => {
    if (!user || !newFilterName.trim()) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('export_filters')
        .insert({
          user_id: user.id,
          name: newFilterName.trim(),
          columns: columns.filter(col => col.checked).map(col => col.key)
        })
        .select()
        .single();

      if (error) throw error;

      const newFilter: SavedFilter = {
        id: data.id,
        name: data.name,
        columns: data.columns as string[]
      };

      setSavedFilters([...savedFilters, newFilter]);
      setNewFilterName('');
      setShowSaveForm(false);
    } catch (err) {
      console.error('Error saving filter:', err);
      setError('Erreur lors de la sauvegarde du filtre');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFilter = async (id: string) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('export_filters')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSavedFilters(savedFilters.filter(f => f.id !== id));
      if (selectedFilter === id) {
        setSelectedFilter('');
      }
    } catch (err) {
      console.error('Error deleting filter:', err);
      setError('Erreur lors de la suppression du filtre');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadFilter = (filterId: string) => {
    const filter = savedFilters.find(f => f.id === filterId);
    if (!filter) return;

    setColumns(columns.map(col => ({
      ...col,
      checked: filter.columns.includes(col.key)
    })));
    setSelectedFilter(filterId);
  };

  const handleExport = () => {
    const selectedColumns = columns.filter(col => col.checked);
    
    // Créer l'en-tête
    const headers = selectedColumns.map(col => col.label);
    
    // Créer les lignes de données avec formatage français des nombres
    const rows = transactions.map(transaction => 
      selectedColumns.map(col => {
        const value = col.getValue(transaction);
        
        // Convertir les nombres avec des points en nombres avec des virgules
        if (col.key === 'sale_price' || 
            col.key === 'fees_with_tax' || 
            col.key === 'fees_without_tax' || 
            col.key === 'net_fees_without_tax') {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            return numValue.toFixed(2).replace('.', ',');
          }
        }
        
        return value;
      })
    );

    // Créer le workbook et la worksheet
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');

    // Télécharger le fichier Excel
    XLSX.writeFile(wb, `transactions_export_${new Date().toISOString().slice(0, 10)}.xlsx`);

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>

        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Exporter les transactions
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Sélectionnez les colonnes à inclure dans l'export CSV
            </p>
          </div>

          {/* Filtres sauvegardés */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Filtres sauvegardés</h3>
              <button
                onClick={() => setShowSaveForm(!showSaveForm)}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
              >
                <Save size={16} />
                <span>Sauvegarder la sélection</span>
              </button>
            </div>

            {showSaveForm && (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newFilterName}
                  onChange={(e) => setNewFilterName(e.target.value)}
                  placeholder="Nom du filtre"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
                <button
                  onClick={handleSaveFilter}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Sauvegarder'
                  )}
                </button>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}

            {savedFilters.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {savedFilters.map((filter) => (
                  <div
                    key={filter.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      selectedFilter === filter.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <button
                      onClick={() => handleLoadFilter(filter.id)}
                      className="flex-1 text-left"
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {filter.name}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {filter.columns.length} colonnes
                      </span>
                    </button>
                    <button
                      onClick={() => handleDeleteFilter(filter.id)}
                      disabled={loading}
                      className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between pb-4 border-b">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={columns.every(col => col.checked)}
                  onChange={handleToggleAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm font-medium text-gray-700">
                  Tout sélectionner
                </label>
              </div>
              <span className="text-sm text-gray-500">
                {columns.filter(col => col.checked).length} colonnes sélectionnées
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {columns.map((column) => (
                <div key={column.key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={column.checked}
                    onChange={() => handleToggleColumn(column.key)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="text-sm text-gray-700">
                    {column.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <FileDown size={18} />
              <span>Exporter en Excel</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}